import React, { useMemo, useState } from "react";
import { useDemoPage } from "@/lib/useDemoPage";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Upload, Phone, Mail, Shield, FileText, ArrowRight, Building2, Copy, Loader2, Calendar, Info } from "lucide-react";

/**
 * Demo AXA – Flow de souscription assurance œuvre d’art
 * ----------------------------------------------------
 * • Étape 1 : formulaire assuré + œuvre + garanties
 * • Étape 2 : récap → envoi courtier AXA (payload JSON simulé)
 * • Étape 3 : suivi courtier (appel programmé) + devis simulé (PDF faux)
 *
 * NB: Tout est simulé côté client pour une démo de rendez-vous.
 *     Branchez ici votre webhook/email backend au moment du passage en production.
 */

const Field = ({ label, children, hint, required }) => (
  <label className="block">
    <div className="mb-1 flex items-center gap-2 text-sm font-medium">
      <span>{label}{required && <span className="text-red-600"> *</span>}</span>
      {hint && (
        <span className="text-gray-500 text-xs flex items-center gap-1"><Info size={14}/> {hint}</span>
      )}
    </div>
    {children}
  </label>
);

const Step = ({ n, title, subtitle, active, done, icon }) => (
  <div className={`relative pl-10 ${!active && !done ? "opacity-60" : ""}`}>
    <div className={`absolute left-0 top-0 w-8 h-8 rounded-full flex items-center justify-center border ${active ? "bg-black text-white" : done ? "bg-green-600 text-white border-green-600" : "bg-white"}`}>
      {done ? <Check size={18}/> : icon ? icon : <span className="text-xs font-semibold">{n}</span>}
    </div>
    <div className="font-semibold">{title}</div>
    {subtitle && <div className="text-sm text-gray-600">{subtitle}</div>}
  </div>
);

const PriceRow = ({ label, amount, bold }) => (
  <div className={`flex justify-between ${bold ? "font-semibold" : ""}`}>
    <span>{label}</span>
    <span>{amount.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</span>
  </div>
);

function usePremium({ valeurEstimee, garantie, securite, alerteSurSite, transport }) {
  // Modèle tarifaire fictif pour la démo
  return useMemo(() => {
    const baseRate = 0.006; // 0,6% du capital assuré
    const g = garantie === "tous_risques" ? 1 : 0.8;
    const s = securite === "elevee" ? 0.85 : securite === "moyenne" ? 1 : 1.25;
    const a = alerteSurSite ? 0.95 : 1;
    const t = transport ? 1.15 : 1;
    const primeAnnuelle = Math.max(90, valeurEstimee * baseRate * g * s * a * t);
    const fraisCourtier = Math.min(120, primeAnnuelle * 0.08);
    const tva = (primeAnnuelle + fraisCourtier) * 0.2;
    const total = primeAnnuelle + fraisCourtier + tva;
    return { primeAnnuelle, fraisCourtier, tva, total };
  }, [valeurEstimee, garantie, securite, alerteSurSite, transport]);
}

export default function AXAInsuranceDemo() {
  useDemoPage("assureur-axa", { noindex: true });
  const [step, setStep] = useState(1);
  const [sending, setSending] = useState(false);
  const [brokerStatus, setBrokerStatus] = useState(/** 'waiting'|'called'|'quoted' */"waiting");
  const [meeting, setMeeting] = useState(null);

  const [form, setForm] = useState({
    // Assuré
    civilite: "Mme",
    prenom: "",
    nom: "",
    email: "",
    telephone: "",
    adresse: "",
    ville: "",
    pays: "",

    // Œuvre
    artiste: "",
    titre: "",
    annee: "",
    categorie: "Peinture",
    valeurEstimee: 50000,
    photos: [],

    // Garanties
    garantie: "tous_risques", // tous_risques | clou_a_clou
    securite: "moyenne", // faible | moyenne | elevee
    franchise: 500,
    transport: false,
    alerteSurSite: true,

    // Conformité
    consentement: true,
  });

  const premium = usePremium({
    valeurEstimee: Number(form.valeurEstimee || 0),
    garantie: form.garantie,
    securite: form.securite,
    alerteSurSite: form.alerteSurSite,
    transport: form.transport,
  });

  const disabledNext = () => {
    if (!form.prenom || !form.nom || !form.email || !form.telephone || !form.artiste || !form.titre || !form.valeurEstimee) return true;
    if (!form.consentement) return true;
    return false;
  };

  const payload = useMemo(() => ({
    partner: {
      name: "AXA – Réseau Courtage Art",
      code: "AXA_ART_DEMO",
    },
    applicant: {
      salutation: form.civilite,
      first_name: form.prenom,
      last_name: form.nom,
      email: form.email,
      phone: form.telephone,
      address: `${form.adresse}, ${form.ville}, ${form.pays}`,
    },
    object: {
      category: form.categorie,
      artist: form.artiste,
      title: form.titre,
      year: form.annee || null,
      declared_value_eur: Number(form.valeurEstimee),
      security_level: form.securite,
      transport_needed: form.transport,
    },
    coverage: {
      plan: form.garantie,
      deductible_eur: Number(form.franchise),
      onsite_alarm: form.alerteSurSite,
    },
    pricing_preview: premium,
    meta: {
      demo: true,
      submitted_at_iso: new Date().toISOString(),
      ui_origin: "artkiv/insurance-demo",
      callback_url: window.location.href,
    },
  }), [form, premium]);

  const sendToBroker = async () => {
    setSending(true);
    // Simulation réseau
    await new Promise(r => setTimeout(r, 900));
    // Ici vous appelleriez votre backend / webhook courtier en production.
    setSending(false);
    setStep(3);
  };

  const simulateCallBooking = () => {
    const in2h = new Date(Date.now() + 2 * 60 * 60 * 1000);
    setMeeting(in2h);
    setBrokerStatus("called");
  };

  const simulateQuote = () => {
    setBrokerStatus("quoted");
  };

  const handleDownloadQuote = () => {
    window.alert("Démo : le PDF de devis serait généré par le backend Artkiv / courtier.");
  };

  const handleSubscribe = () => {
    window.alert("Démo : la souscription ouvrirait le parcours sécurisé AXA / Stripe / courtier.");
  };

  const onFile = (files) => {
    const arr = Array.from(files || []);
    setForm(f => ({ ...f, photos: [...f.photos, ...arr.map(fx => ({ name: fx.name, size: fx.size }))] }));
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-black text-white grid place-items-center"><Shield size={18}/></div>
            <div>
              <div className="text-xl font-bold">ARTKIV × AXA</div>
              <div className="text-gray-600 text-sm">Souscription assurance œuvres d’art – Démonstration</div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2 border rounded-full px-3 py-1 bg-white">
            <Building2 size={16}/>
            <span className="text-sm">Courtier partenaire : <b>AXA</b></span>
          </div>
        </div>

        {/* Progress */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Step n={1} title="Votre demande" subtitle="Assuré • Œuvre • Garanties" active={step===1} done={step>1} />
          <Step n={2} title="Transmission sécurisée" subtitle="Envoi courtier & accusé" active={step===2} done={step>2} icon={<Mail size={16}/>}/>
          <Step n={3} title="Suivi courtier" subtitle="Appel & devis" active={step===3} done={false} icon={<Phone size={16}/>}/>
        </div>

        {/* Card */}
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow p-5">
              {step === 1 && (
                <AnimatePresence mode="wait">
                  <motion.div key="form" initial={{opacity:0, y:8}} animate={{opacity:1, y:0}} exit={{opacity:0, y:-8}}>
                    <div className="grid md:grid-cols-2 gap-4">
                      <Field label="Civilité">
                        <select value={form.civilite} onChange={e=>setForm({...form, civilite:e.target.value})} className="w-full border rounded-lg px-3 py-2">
                          <option>Mme</option>
                          <option>M.</option>
                          <option>Autre</option>
                        </select>
                      </Field>
                      <Field label="Prénom" required>
                        <input className="w-full border rounded-lg px-3 py-2" value={form.prenom} onChange={e=>setForm({...form, prenom:e.target.value})}/>
                      </Field>
                      <Field label="Nom" required>
                        <input className="w-full border rounded-lg px-3 py-2" value={form.nom} onChange={e=>setForm({...form, nom:e.target.value})}/>
                      </Field>
                      <Field label="Email" required>
                        <input type="email" className="w-full border rounded-lg px-3 py-2" value={form.email} onChange={e=>setForm({...form, email:e.target.value})}/>
                      </Field>
                      <Field label="Téléphone" required>
                        <input className="w-full border rounded-lg px-3 py-2" value={form.telephone} onChange={e=>setForm({...form, telephone:e.target.value})}/>
                      </Field>
                    </div>

                    <div className="mt-6 grid md:grid-cols-2 gap-4">
                      <Field label="Adresse">
                        <input className="w-full border rounded-lg px-3 py-2" value={form.adresse} onChange={e=>setForm({...form, adresse:e.target.value})}/>
                      </Field>
                      <Field label="Ville">
                        <input className="w-full border rounded-lg px-3 py-2" value={form.ville} onChange={e=>setForm({...form, ville:e.target.value})}/>
                      </Field>
                      <Field label="Pays">
                        <input className="w-full border rounded-lg px-3 py-2" value={form.pays} onChange={e=>setForm({...form, pays:e.target.value})}/>
                      </Field>
                    </div>

                    <div className="mt-8 border-t pt-6 grid md:grid-cols-2 gap-4">
                      <Field label="Artiste" required>
                        <input className="w-full border rounded-lg px-3 py-2" value={form.artiste} onChange={e=>setForm({...form, artiste:e.target.value})} placeholder="Ex : Pierre Soulages"/>
                      </Field>
                      <Field label="Titre de l’œuvre" required>
                        <input className="w-full border rounded-lg px-3 py-2" value={form.titre} onChange={e=>setForm({...form, titre:e.target.value})}/>
                      </Field>
                      <Field label="Année">
                        <input className="w-full border rounded-lg px-3 py-2" value={form.annee} onChange={e=>setForm({...form, annee:e.target.value})}/>
                      </Field>
                      <Field label="Catégorie">
                        <select className="w-full border rounded-lg px-3 py-2" value={form.categorie} onChange={e=>setForm({...form, categorie:e.target.value})}>
                          <option>Peinture</option>
                          <option>Sculpture</option>
                          <option>Dessin / Papier</option>
                          <option>Photographie</option>
                          <option>Edition / Multiple</option>
                          <option>Autre</option>
                        </select>
                      </Field>
                      <Field label="Valeur estimée (€)" required hint="base pour la prime">
                        <input type="number" min={1000} step={100} className="w-full border rounded-lg px-3 py-2" value={form.valeurEstimee} onChange={e=>setForm({...form, valeurEstimee:e.target.value})}/>
                      </Field>
                      <Field label="Téléverser des photos (démo)">
                        <div className="border rounded-lg p-3 flex items-center gap-3 justify-between">
                          <div className="text-sm text-gray-600">{form.photos.length ? `${form.photos.length} fichier(s) ajouté(s)` : "Aucun fichier"}</div>
                          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer hover:bg-gray-50">
                            <Upload size={16}/> Importer
                            <input type="file" className="hidden" multiple onChange={(e)=>onFile(e.target.files)}/>
                          </label>
                        </div>
                      </Field>
                    </div>

                    <div className="mt-8 border-t pt-6 grid md:grid-cols-2 gap-4">
                      <Field label="Type de garantie">
                        <div className="flex gap-2">
                          {[
                            {k:"tous_risques", label:"Tous risques"},
                            {k:"clou_a_clou", label:"Clou à clou"},
                          ].map(opt => (
                            <button key={opt.k} onClick={()=>setForm({...form, garantie:opt.k})} type="button" className={`px-3 py-2 rounded-lg border ${form.garantie===opt.k?"bg-black text-white":"bg-white"}`}>{opt.label}</button>
                          ))}
                        </div>
                      </Field>
                      <Field label="Niveau de sécurité du lieu">
                        <select className="w-full border rounded-lg px-3 py-2" value={form.securite} onChange={e=>setForm({...form, securite:e.target.value})}>
                          <option value="faible">Faible</option>
                          <option value="moyenne">Moyenne</option>
                          <option value="elevee">Élevée</option>
                        </select>
                      </Field>
                      <Field label="Franchise (€)">
                        <input type="number" className="w-full border rounded-lg px-3 py-2" value={form.franchise} onChange={e=>setForm({...form, franchise:e.target.value})}/>
                      </Field>
                      <Field label="Options">
                        <div className="flex gap-6 text-sm">
                          <label className="inline-flex items-center gap-2">
                            <input type="checkbox" checked={form.transport} onChange={e=>setForm({...form, transport:e.target.checked})}/> Transport
                          </label>
                          <label className="inline-flex items-center gap-2">
                            <input type="checkbox" checked={form.alerteSurSite} onChange={e=>setForm({...form, alerteSurSite:e.target.checked})}/> Alarme sur site
                          </label>
                        </div>
                      </Field>
                    </div>

                    <div className="mt-6 bg-gray-50 rounded-xl p-4">
                      <div className="text-sm text-gray-600 mb-2">Projection tarifaire (démo)</div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <PriceRow label="Prime annuelle" amount={premium.primeAnnuelle}/>
                        <PriceRow label="Frais de courtage" amount={premium.fraisCourtier}/>
                        <PriceRow label="TVA (20%)" amount={premium.tva}/>
                        <PriceRow label="Total TTC" amount={premium.total} bold/>
                      </div>
                    </div>

                    <div className="mt-6 flex items-start gap-3">
                      <input type="checkbox" checked={form.consentement} onChange={e=>setForm({...form, consentement:e.target.checked})}/>
                      <div className="text-sm text-gray-700">J’autorise Artkiv à transmettre ces informations à son courtier partenaire AXA afin d’obtenir une proposition d’assurance.</div>
                    </div>

                    <div className="mt-6 flex justify-end gap-3">
                      <button disabled className="px-4 py-2 rounded-lg border">Enregistrer le brouillon</button>
                      <button disabled={disabledNext()} onClick={()=>setStep(2)} className={`px-4 py-2 rounded-lg text-white flex items-center gap-2 ${disabledNext()?"bg-gray-400":"bg-black hover:opacity-90"}`}>
                        Continuer <ArrowRight size={16}/>
                      </button>
                    </div>
                  </motion.div>
                </AnimatePresence>
              )}

              {step === 2 && (
                <AnimatePresence>
                  <motion.div key="send" initial={{opacity:0, y:8}} animate={{opacity:1, y:0}} exit={{opacity:0, y:-8}}>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-black text-white grid place-items-center"><Mail/></div>
                      <div>
                        <div className="font-semibold text-lg mb-1">Prêt à transmettre au courtier AXA</div>
                        <div className="text-gray-600">Nous envoyons un résumé conforme (JSON) avec chiffrement TLS. Le courtier vous rappelle pour qualifier le risque et finaliser le devis.</div>
                      </div>
                    </div>

                    <div className="mt-6">
                      <div className="text-sm font-medium mb-2">Payload transmis (aperçu démo)</div>
                      <pre className="bg-gray-900 text-gray-100 rounded-xl p-4 overflow-auto max-h-72 text-xs">
{JSON.stringify(payload, null, 2)}
                      </pre>
                      <div className="flex gap-2 mt-2">
                        <button onClick={()=>navigator.clipboard.writeText(JSON.stringify(payload, null, 2))} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border"><Copy size={16}/> Copier</button>
                        <button onClick={sendToBroker} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-white bg-black">
                          {sending ? (<><Loader2 className="animate-spin" size={16}/> Envoi…</>) : (<>Envoyer au courtier</>)}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              )}

              {step === 3 && (
                <AnimatePresence>
                  <motion.div key="follow" initial={{opacity:0, y:8}} animate={{opacity:1, y:0}} exit={{opacity:0, y:-8}}>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <div className="font-semibold mb-2">Statut courtier</div>
                        <div className="space-y-3">
                          <div className={`rounded-xl border p-3 ${brokerStatus!=="waiting"?"opacity-60":""}`}>
                            <div className="flex items-center gap-2"><Mail size={16}/> Dossier transmis</div>
                            <div className="text-sm text-gray-600">Accusé de réception automatique.</div>
                          </div>
                          <div className={`rounded-xl border p-3 ${brokerStatus==="called"||brokerStatus==="quoted"?"":"opacity-60"}`}>
                            <div className="flex items-center gap-2"><Phone size={16}/> Appel de qualification</div>
                            {meeting ? (
                              <div className="text-sm text-gray-600 flex items-center gap-2"><Calendar size={14}/> Programmé : {meeting.toLocaleString("fr-FR")}</div>
                            ) : (
                              <div className="text-sm text-gray-600">—</div>
                            )}
                            <div className="mt-2 flex gap-2">
                              <button onClick={simulateCallBooking} className="px-3 py-2 rounded-lg border">Simuler : prise de RDV</button>
                            </div>
                          </div>
                          <div className={`rounded-xl border p-3 ${brokerStatus==="quoted"?"":"opacity-60"}`}>
                            <div className="flex items-center gap-2"><FileText size={16}/> Devis envoyé</div>
                            <div className="text-sm text-gray-600">PDF envoyé par email + lien de paiement.</div>
                            <div className="mt-2 flex gap-2">
                              <button onClick={simulateQuote} className="px-3 py-2 rounded-lg border">Simuler : générer le devis</button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <div className="font-semibold mb-2">Devis (aperçu démo)</div>
                        <div className="rounded-2xl border p-4 bg-white">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-sm text-gray-500">Assureur</div>
                            <div className="text-sm font-semibold">AXA – Courtage Art</div>
                          </div>
                          <div className="text-sm text-gray-600">Objet : {form.artiste ? `${form.artiste} — ${form.titre}` : "Œuvre d’art"}</div>
                          <div className="text-sm text-gray-600 mb-4">Valeur déclarée : {Number(form.valeurEstimee||0).toLocaleString("fr-FR", {style:"currency", currency:"EUR"})}</div>
                          <div className="space-y-2 text-sm">
                            <PriceRow label="Prime annuelle" amount={premium.primeAnnuelle}/>
                            <PriceRow label="Frais de courtage" amount={premium.fraisCourtier}/>
                            <PriceRow label="TVA (20%)" amount={premium.tva}/>
                            <div className="pt-2 border-t">
                              <PriceRow label="Total TTC à régler" amount={premium.total} bold/>
                            </div>
                          </div>
                          <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-gray-600">
                            <div>Plan : <b>{form.garantie === "tous_risques" ? "Tous risques" : "Clou à clou"}</b></div>
                            <div>Franchise : {form.franchise} €</div>
                            <div>Sécurité : {form.securite}</div>
                            <div>Transport : {form.transport ? "Oui" : "Non"}</div>
                          </div>
                          <div className="mt-4 flex gap-2">
                            <button
                              type="button"
                              onClick={handleDownloadQuote}
                              className="px-3 py-2 rounded-lg border inline-flex items-center gap-2"
                            >
                              <FileText size={16}/> Télécharger le PDF
                            </button>
                            <button
                              type="button"
                              onClick={handleSubscribe}
                              className="px-3 py-2 rounded-lg bg-black text-white inline-flex items-center gap-2"
                            >
                              <Shield size={16}/> Souscrire
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              )}
            </div>
          </div>

          {/* Récap latéral */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow p-5">
              <div className="font-semibold mb-2">Résumé</div>
              <div className="text-sm text-gray-700 space-y-1">
                <div>{form.civilite} {form.prenom} {form.nom}</div>
                <div className="text-gray-500">{form.email} · {form.telephone}</div>
                <div className="text-gray-500">{[form.adresse, form.ville, form.pays].filter(Boolean).join(", ")}</div>
              </div>
              <div className="mt-3 text-sm">
                <div className="font-medium">Œuvre</div>
                <div className="text-gray-600">{form.artiste || "—"} • {form.titre || "—"} {form.annee && `(${form.annee})`}</div>
                <div className="text-gray-600">{form.categorie}</div>
                <div className="text-gray-600">Valeur : {Number(form.valeurEstimee||0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</div>
              </div>
              <div className="mt-3 text-sm">
                <div className="font-medium">Garanties</div>
                <div className="text-gray-600">{form.garantie === "tous_risques" ? "Tous risques" : "Clou à clou"}, franchise {form.franchise} €</div>
                <div className="text-gray-600">Sécurité : {form.securite} • Transport : {form.transport?"Oui":"Non"}</div>
              </div>
              <div className="mt-4 bg-gray-50 rounded-xl p-3">
                <PriceRow label="Total estimé TTC" amount={premium.total} bold/>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow p-5">
              <div className="font-semibold mb-2">Pourquoi AXA ?</div>
              <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                <li>Réseau courtage spécialisé art et objets de collection</li>
                <li>Gestion sinistre premium et expertise internationale</li>
                <li>Options d’extension (transport, exposition, prêt)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
